import React, { ReactNode, useEffect, useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { authApi } from '../lib/api';
import { useSystemAdminAccess } from '../hooks/useSystemAdminAccess';

interface LayoutProps {
  children?: ReactNode;
  skipTenantRedirect?: boolean;
}

export default function Layout({ children, skipTenantRedirect = false }: LayoutProps) {
  const { user, tenantContext, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  
  // Check if user is an allowlisted SystemAdmin (for Admin nav visibility)
  const { isSystemAdmin } = useSystemAdminAccess();

  // Redirect to tenant selection if no tenant context
  useEffect(() => {
    // Don't redirect from /setup or /tenants pages
    if (location.pathname === '/setup' || location.pathname === '/tenants') {
      return;
    }
    if (skipTenantRedirect || tenantContext) {
      return;
    }
    const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
    navigate(`/tenants?returnTo=${returnTo}`, { replace: true });
  }, [tenantContext, navigate, location.pathname, location.search, skipTenantRedirect]);

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileSidebarOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMobileSidebarOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [mobileSidebarOpen]);

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore errors
    }
    logout();
    navigate('/login');
  };

  const isActiveRoute = (to: string) =>
    location.pathname === to || location.pathname.startsWith(`${to}/`);

  const navGroups = [
    {
      label: 'Overview',
      links: [
        { to: '/dashboard', label: 'Dashboard' },
      ],
    },
    {
      label: 'Audit',
      links: [
        { to: '/timeline', label: 'Timeline' },
        { to: '/coverage', label: 'Coverage' },
        { to: '/evidence', label: 'Evidence' },
        { to: '/reviews', label: 'Reviews' },
      ],
    },
    {
      label: 'Compliance',
      links: [
        { to: '/controls', label: 'Controls' },
        { to: '/drift', label: 'Drift' },
        { to: '/exceptions', label: 'Exceptions' },
      ],
    },
    {
      label: 'Setup',
      links: [
        { to: '/connections', label: 'Connections' },
        { to: '/tools', label: 'Tools' },
        { to: '/settings', label: 'Settings' },
        ...(isSystemAdmin ? [{ to: '/admin', label: 'Operator' }] : []),
      ],
    },
  ];

  return (
    <div className="h-screen overflow-hidden bg-slate-900 flex">
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex flex-col bg-slate-800 border-r border-slate-700 transition-all duration-200 ${
        sidebarOpen ? 'w-64' : 'w-20'
      } flex-shrink-0`}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-700">
          <Link to="/dashboard" className="flex items-center gap-3 flex-shrink-0">
            <div className="w-8 h-8 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            {sidebarOpen && <span className="text-sm font-semibold text-white tracking-tight whitespace-nowrap">EvidentTrail</span>}
          </Link>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white"
            title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={sidebarOpen ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
            </svg>
          </button>
        </div>



        {/* Grouped Nav */}
        <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-4">
          {navGroups.map((group) => (
            <div key={group.label}>
              {sidebarOpen && (
                <div className="px-3 mb-1 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  {group.label}
                </div>
              )}
              <div className="space-y-0.5">
                {group.links.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      isActiveRoute(link.to)
                        ? 'bg-primary-600/20 text-primary-300'
                        : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                    }`}
                    title={!sidebarOpen ? link.label : undefined}
                  >
                    {sidebarOpen ? (
                      <>
                        <span className={`flex-shrink-0 w-4 h-4 flex items-center justify-center ${
                          isActiveRoute(link.to) ? 'text-primary-400' : 'text-slate-500'
                        }`}>
                          {getNavIcon(link.label)}
                        </span>
                        <span>{link.label}</span>
                        {isActiveRoute(link.to) && (
                          <span className="ml-auto w-1.5 h-1.5 rounded-full bg-primary-400"></span>
                        )}
                      </>
                    ) : (
                      <span className="w-5 h-5 flex items-center justify-center mx-auto">{getNavIcon(link.label)}</span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* User / Tenant Footer */}
        <div className="border-t border-slate-700 p-3">
          {sidebarOpen ? (
            <div className="space-y-2">
              {tenantContext && (
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-white truncate">{tenantContext.tenantName}</div>
                    <div className="text-xs text-slate-400">{tenantContext.role}</div>
                  </div>
                  <Link
                    to="/tenants"
                    className="text-xs text-primary-400 hover:text-primary-300 whitespace-nowrap ml-2 flex-shrink-0"
                  >
                    Switch
                  </Link>
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="text-xs text-slate-400 truncate min-w-0 mr-2">{user?.email}</div>
                <button
                  onClick={handleLogout}
                  className="text-xs text-slate-400 hover:text-white whitespace-nowrap flex-shrink-0"
                >
                  Logout
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Link to="/tenants" className="p-1 text-slate-400 hover:text-white" title="Switch Tenant">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6" />
                </svg>
              </Link>
              <button onClick={handleLogout} className="p-1 text-slate-400 hover:text-white" title="Logout">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h8a2 2 0 012 2v3" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 15v3a2 2 0 01-2 2H3" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 12h5m0 0l-2-2m2 2l-2 2" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside className={`fixed top-0 left-0 h-screen w-64 bg-slate-800 border-r border-slate-700 transform transition-transform duration-200 z-50 lg:hidden flex flex-col ${
        mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-700">
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-500 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <span className="text-sm font-semibold text-white tracking-tight">EvidentTrail</span>
          </Link>
          <button
            onClick={() => setMobileSidebarOpen(false)}
            className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>



        {/* All Nav Items - Grouped */}
        <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-4">
          {navGroups.map((group) => (
            <div key={group.label}>
              <div className="px-3 mb-1 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                {group.label}
              </div>
              <div className="space-y-0.5">
                {group.links.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setMobileSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActiveRoute(link.to)
                        ? 'bg-primary-600/20 text-primary-300'
                        : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                    }`}
                  >
                    <span className={`w-4 h-4 flex-shrink-0 ${isActiveRoute(link.to) ? 'text-primary-400' : 'text-slate-500'}`}>
                      {getNavIcon(link.label)}
                    </span>
                    <span>{link.label}</span>
                    {isActiveRoute(link.to) && (
                      <span className="ml-auto w-1.5 h-1.5 rounded-full bg-primary-400"></span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* User / Tenant Footer */}
        <div className="border-t border-slate-700 p-3 space-y-2">
          {tenantContext && (
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-xs font-semibold text-white truncate">{tenantContext.tenantName}</div>
                <div className="text-xs text-slate-400">{tenantContext.role}</div>
              </div>
              <Link
                to="/tenants"
                onClick={() => setMobileSidebarOpen(false)}
                className="text-xs text-primary-400 hover:text-primary-300 whitespace-nowrap ml-2 flex-shrink-0"
              >
                Switch
              </Link>
            </div>
          )}
          <div className="flex items-center justify-between">
            <div className="text-xs text-slate-400 truncate min-w-0 mr-2">{user?.email}</div>
            <button
              onClick={handleLogout}
              className="text-xs text-slate-400 hover:text-white whitespace-nowrap flex-shrink-0"
            >
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <div className="lg:hidden flex items-center gap-3 h-14 px-4 bg-slate-800 border-b border-slate-700 flex-shrink-0">
          <button
            onClick={() => setMobileSidebarOpen(true)}
            className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
            aria-label="Open navigation"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-500 rounded-lg flex items-center justify-center">
              <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <span className="text-sm font-semibold text-white">EvidentTrail</span>
          </div>
        </div>

        {/* Main Content Area */}
        <main className="flex-1 overflow-auto px-4 sm:px-6 lg:px-8 py-8">
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
}

function getNavIcon(label: string): React.ReactNode {
  const cls = 'w-4 h-4';
  const icons: Record<string, React.ReactNode> = {
    'Dashboard': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
    'Timeline': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h10M4 18h6"/></svg>,
    'Coverage': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>,
    'Evidence': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>,
    'Reviews': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>,
    'Controls': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>,
    'Drift': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>,
    'Exceptions': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>,
    'Connections': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/></svg>,
    'Tools': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>,
    'Settings': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"/></svg>,
    'Operator': <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>,
  };
  return icons[label] ?? <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><circle cx="12" cy="12" r="4"/></svg>;
}
